import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Terminal,
  FileCode,
  Package,
  Sparkle,
  FloppyDisk,
  Cpu,
  CheckCircle,
  Warning,
  Archive,
} from "@phosphor-icons/react";

const OUTPUT_TYPES = [
  { id: "script", label: "Shell Script", icon: Terminal, hint: "Bash automation" },
  { id: "profile", label: "Config Profile", icon: FileCode, hint: ".mobileconfig" },
  { id: "software", label: "Deploy Software", icon: Package, hint: "Git packages" },
];

const MODELS = [
  { id: "gpt-5.4", label: "GPT-5.4" },
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { id: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro" },
];

export default function Generator() {
  const location = useLocation();
  const [providers, setProviders] = useState([]);
  const [software, setSoftware] = useState([]);
  const [provider, setProvider] = useState("jamf");
  const [outputType, setOutputType] = useState("script");
  const [model, setModel] = useState("gpt-5.4");
  const [prompt, setPrompt] = useState("");
  const [softwareId, setSoftwareId] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [validation, setValidation] = useState(null);
  const [title, setTitle] = useState("");
  const [projects, setProjects] = useState([]);
  const [saveTarget, setSaveTarget] = useState("new");
  const [aiOwn, setAiOwn] = useState(null);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/software").then((r) => setSoftware(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/settings/ai").then((r) => setAiOwn(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.software) {
      const sw = location.state.software;
      setOutputType("software");
      setSoftwareId(sw.id);
      setPrompt(`Deploy ${sw.name} with recommended defaults.`);
      setTitle(`${sw.name} deployment`);
    }
  }, [location.state]);

  const validate = async (content, ot) => {
    try {
      const { data } = await api.post("/validate", { content, output_type: ot });
      setValidation(data);
    } catch {
      /* non-blocking */
    }
  };

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Describe what you want to build");
      return;
    }
    setLoading(true);
    setResult(null);
    setValidation(null);
    let meta = null;
    let acc = "";
    try {
      const resp = await fetch(`${API}/generate/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          prompt,
          provider,
          output_type: outputType,
          model,
          software_id: outputType === "software" ? softwareId : null,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error("Generation failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.type === "meta") {
            meta = payload;
            setResult({
              content: "",
              language: payload.language,
              filename: payload.filename,
              deploy_notes: payload.deploy_notes,
              provider: payload.provider,
              output_type: payload.output_type,
            });
          } else if (payload.type === "delta") {
            acc += payload.content;
            setResult((r) => (r ? { ...r, content: acc } : r));
          } else if (payload.type === "done") {
            acc = payload.content;
            setResult((r) => (r ? { ...r, content: acc } : r));
          } else if (payload.type === "error") {
            throw new Error(payload.message);
          }
        }
      }
      if (!title) setTitle(prompt.slice(0, 48));
      toast.success("Artifact generated");
      if (meta) validate(acc, meta.output_type);
    } catch (err) {
      toast.error(err.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadBundle = async () => {
    if (!result) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: title || "Untitled",
          provider: result.provider,
          output_type: result.output_type,
          content: result.content,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${result.provider}-bundle.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch {
      toast.error("Bundle download failed");
    } finally {
      setBundling(false);
    }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: title || "Untitled",
          provider: result.provider,
          output_type: result.output_type,
          prompt,
          content: result.content,
          language: result.language,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        setSaveTarget(data.id);
        toast.success("Saved as new project");
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: result.content,
          language: result.language,
          filename: result.filename,
          deploy_notes: result.deploy_notes,
          prompt,
          note: `v${(projects.find((p) => p.id === saveTarget)?.versions?.length || 0) + 1}`,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <Sparkle size={22} className="text-[#00F0FF]" weight="fill" /> AI Generator
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">
          Describe your goal — get deploy-ready artifacts for your MDM.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#27272A]">
        {/* Left: config */}
        <div className="bg-[#0A0A0B] p-8 space-y-8">
          <div>
            <label className="block font-mono text-xs text-[#71717A] mb-3">01 · OUTPUT TYPE</label>
            <div className="grid grid-cols-3 gap-2">
              {OUTPUT_TYPES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`output-type-${t.id}`}
                  onClick={() => setOutputType(t.id)}
                  className={`flex flex-col items-start gap-2 p-3 border text-left transition-colors duration-200 ${
                    outputType === t.id
                      ? "border-[#00F0FF] bg-[#00F0FF]/5"
                      : "border-[#27272A] hover:border-[#3f3f46]"
                  }`}
                >
                  <t.icon size={18} className={outputType === t.id ? "text-[#00F0FF]" : "text-[#A1A1AA]"} />
                  <span className="text-xs">{t.label}</span>
                  <span className="font-mono text-[10px] text-[#71717A]">{t.hint}</span>
                </button>
              ))}
            </div>
          </div>

          {outputType === "software" && (
            <div>
              <label className="block font-mono text-xs text-[#71717A] mb-3">SELECT PACKAGE</label>
              <select
                data-testid="software-select"
                value={softwareId || ""}
                onChange={(e) => setSoftwareId(e.target.value)}
                className="w-full bg-[#050505] border border-[#27272A] px-3 py-2.5 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
              >
                <option value="">— choose a tool —</option>
                {software.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · {s.repo}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block font-mono text-xs text-[#71717A] mb-3">02 · MDM PROVIDER</label>
            <div className="grid grid-cols-2 gap-2">
              {providers.map((p) => (
                <button
                  key={p.id}
                  data-testid={`provider-${p.id}`}
                  onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2.5 p-3 border text-left transition-colors duration-200 ${
                    provider === p.id
                      ? "border-[#00F0FF] bg-[#00F0FF]/5"
                      : "border-[#27272A] hover:border-[#3f3f46]"
                  }`}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                  <div className="min-w-0">
                    <div className="text-xs truncate">{p.name}</div>
                    <div className="font-mono text-[10px] text-[#71717A] truncate">{p.tagline}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block font-mono text-xs text-[#71717A] mb-3 flex items-center gap-1.5">
              <Cpu size={13} /> 03 · MODEL
            </label>
            {aiOwn?.use_own_key ? (
              <Link to="/app/settings" data-testid="own-key-banner"
                className="flex items-center justify-between gap-2 px-3 py-2.5 border border-[#00FF41]/40 bg-[#00FF41]/5 text-[#00FF41] font-mono text-xs hover:bg-[#00FF41]/10 transition-colors duration-200">
                <span className="flex items-center gap-2"><Cpu size={14} /> Using your key · {aiOwn.provider} · {aiOwn.model}</span>
                <span className="text-[10px] text-[#71717A]">change in Settings →</span>
              </Link>
            ) : (
              <div className="flex gap-2">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`model-${m.id}`}
                    onClick={() => setModel(m.id)}
                    className={`px-3 py-2 border font-mono text-xs transition-colors duration-200 ${
                      model === m.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block font-mono text-xs text-[#71717A] mb-3">04 · DESCRIBE YOUR GOAL</label>
            <textarea
              data-testid="prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              placeholder="e.g. Disable Gatekeeper prompts for internal apps, set the Dock to auto-hide, and enforce FileVault…"
              className="w-full bg-[#050505] border border-[#27272A] px-3 py-3 text-sm font-mono leading-relaxed focus:border-[#00F0FF] focus:outline-none transition-colors duration-200 resize-none"
            />
          </div>

          <button
            data-testid="generate-btn"
            onClick={generate}
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors duration-200 ${
              loading
                ? "tracing-border text-[#0A0A0B]"
                : "bg-[#00F0FF] text-[#0A0A0B] hover:bg-[#00d4e0]"
            }`}
          >
            {loading ? (
              <>Generating<span className="cursor-blink">_</span></>
            ) : (
              <>
                <Sparkle size={16} weight="fill" /> Generate artifact
              </>
            )}
          </button>
        </div>

        {/* Right: output */}
        <div className="bg-[#0A0A0B] p-8">
          {!result && !loading && (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center border border-dashed border-[#27272A] text-center px-8">
              <Terminal size={40} className="text-[#27272A] mb-4" />
              <p className="text-sm text-[#71717A] font-mono">
                Your generated script or profile appears here.
              </p>
            </div>
          )}

          {loading && !result && (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center">
              <div className="font-mono text-sm text-[#00F0FF]">
                forging artifact<span className="cursor-blink">_</span>
              </div>
              <p className="font-mono text-xs text-[#71717A] mt-2">contacting {model}…</p>
            </div>
          )}

          {result && (
            <div className="space-y-4" data-testid="generation-output">
              {!loading && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <select
                      data-testid="save-target-select"
                      value={saveTarget}
                      onChange={(e) => setSaveTarget(e.target.value)}
                      className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                    >
                      <option value="new">＋ New project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          ↳ Add version to: {p.title}
                        </option>
                      ))}
                    </select>
                    {saveTarget === "new" && (
                      <input
                        data-testid="project-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Project title"
                        className="flex-1 bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200"
                      />
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      data-testid="save-project-btn"
                      onClick={save}
                      disabled={saving}
                      className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"
                    >
                      <FloppyDisk size={16} />{" "}
                      {saving ? "Saving…" : saveTarget === "new" ? "Save project" : "Save version"}
                    </button>
                    <button
                      data-testid="bundle-btn"
                      onClick={downloadBundle}
                      disabled={bundling}
                      className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50"
                    >
                      <Archive size={16} /> {bundling ? "Zipping…" : "Bundle"}
                    </button>
                  </div>
                </div>
              )}

              {loading && (
                <div className="font-mono text-xs text-[#00F0FF] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#00FF41] rounded-full animate-pulse" />
                  streaming from {model}<span className="cursor-blink">_</span>
                </div>
              )}

              {!loading && validation && (
                <div
                  data-testid="validation-status"
                  className={`border p-3 flex items-start gap-2 ${
                    validation.valid
                      ? "border-[#00FF41]/40 bg-[#00FF41]/5"
                      : "border-[#FF5F56]/40 bg-[#FF5F56]/5"
                  }`}
                >
                  {validation.valid ? (
                    <CheckCircle size={16} className="text-[#00FF41] mt-0.5" />
                  ) : (
                    <Warning size={16} className="text-[#FF5F56] mt-0.5" />
                  )}
                  <div className="font-mono text-xs">
                    <span className={validation.valid ? "text-[#00FF41]" : "text-[#FF5F56]"}>
                      {validation.valid
                        ? result.output_type === "profile"
                          ? "Valid .mobileconfig — ready to download"
                          : "Looks good — ready to download"
                        : "Validation issues detected"}
                    </span>
                    {validation.errors?.map((e, i) => (
                      <div key={`e${i}`} className="text-[#FF5F56] mt-1">• {e}</div>
                    ))}
                    {validation.warnings?.map((w, i) => (
                      <div key={`w${i}`} className="text-[#A1A1AA] mt-1">• {w}</div>
                    ))}
                  </div>
                </div>
              )}

              <CodeBlock code={result.content} language={result.language} filename={result.filename} />

              {result.deploy_notes && (
                <div className="border border-[#27272A] bg-[#121214] p-4">
                  <div className="font-mono text-xs text-[#00F0FF] mb-2">DEPLOYMENT · {result.provider.toUpperCase()}</div>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{result.deploy_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
